import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBookOpen, FaBars, FaSearch, FaArrowRight, FaChevronLeft, FaChevronRight, FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaUtensils, FaPalette, FaRobot } from 'react-icons/fa';
import blogArticles from '../data/blogArticles';
import { API_BASE_URL } from '../config/api';
import { getAuthorProfilePicture } from '../utils/authorProfiles';

const categories = [
  'All',
  'Language Tips',
  'Persian History',
  'Persian Art',
  'Literature',
  'Modern Iran',
];

const Blog: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 6;

  const featured = blogArticles.find(a => a.is_featured);
  
  // Filter articles based on category and search
  const filteredArticles = blogArticles.filter(a => {
    if (a.is_featured) return false;
    
    // Category filter
    const categoryMatch = activeCategory === 'All' || 
      (a.category && a.category.toLowerCase() === activeCategory.toLowerCase());
    
    // Search filter
    const searchMatch = search === '' || 
      a.title_en.toLowerCase().includes(search.toLowerCase()) ||
      a.summary_en.toLowerCase().includes(search.toLowerCase()) ||
      a.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    return categoryMatch && searchMatch;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const endIndex = startIndex + articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, search]);

  // Newsletter subscribe state (copied from Home)
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<null | { type: 'success' | 'error', message: string }>(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleNewsletterSubscribe = async () => {
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setNewsletterLoading(true);
    setNewsletterStatus(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setNewsletterStatus({ type: 'success', message: data.message || 'Subscribed successfully!' });
        setNewsletterEmail("");
      } else {
        setNewsletterStatus({ type: 'error', message: data.message || 'Subscription failed.' });
      }
    } catch (err) {
      setNewsletterStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen" dir="ltr">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <FaBookOpen className="text-xl gradient-text" />
              <h1 className="text-2xl font-bold text-gray-800">Pirooz</h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-purple-600 font-medium">Home</Link>
              <span className="text-purple-600 font-medium">Blog</span>
              <Link to="/pathways" className="text-gray-700 hover:text-purple-600 font-medium">Courses</Link>
              <Link to="/learning-guide" className="text-gray-700 hover:text-purple-600 font-medium">Resources</Link>
              <Link to="/about" className="text-gray-700 hover:text-purple-600 font-medium">About</Link>
            </nav>
            <button className="md:hidden text-gray-700">
              <FaBars className="text-xl" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-50 to-pink-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">Discover Persian <span className="gradient-text">Culture & Language</span></h2>
            <p className="text-lg text-gray-600 mb-8">Explore our blog to learn about Persian history, art, poetry, and language tips to enhance your Farsi learning journey.</p>
            <div className="relative max-w-xl mx-auto">
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles..." className="w-full py-3 px-6 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm" />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700 transition">
                <FaSearch />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Meet Our AI Persian Teachers</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">Our virtual instructors are available 24/7 to help you learn Farsi - completely free! Each has unique personality and expertise.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Teacher 1 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
            <div className="h-48 bg-gradient-to-r from-purple-100 to-blue-100 flex items-center justify-center">
              <img src="https://i.pravatar.cc/150?img=1" alt="Teacher" className="w-32 h-32 rounded-full border-4 border-white shadow-md" />
            </div>
            <div className="p-6 text-center">
              <h3 className="text-xl font-bold text-gray-800 mb-1">Professor Gol</h3>
              <span className="inline-block px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full mb-3">History & Culture</span>
              <div className="flex flex-col space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-center justify-center">
                  <FaBookOpen className="text-purple-500 mr-2" />
                  <span>Favorite Book: Shahnameh</span>
                </div>
                <div className="flex items-center justify-center">
                  <FaUtensils className="text-purple-500 mr-2" />
                  <span>Loves: Ghormeh Sabzi</span>
                </div>
                <div className="flex items-center justify-center">
                  <FaPalette className="text-purple-500 mr-2" />
                  <span>Favorite Color: Turquoise</span>
                </div>
              </div>
              <p className="text-gray-700 mb-4">"Let me take you on a journey through Persia's glorious past while we practice conversational Farsi!"</p>
              <div className="text-xs text-purple-600 bg-purple-50 px-3 py-2 rounded-lg">
                <FaRobot className="mr-1 inline" /> AI Teacher - Always Free
              </div>
            </div>
          </div>
          {/* Teacher 2 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
            <div className="h-48 bg-gradient-to-r from-pink-100 to-red-100 flex items-center justify-center">
              <img src="https://i.pravatar.cc/150?img=5" alt="Teacher" className="w-32 h-32 rounded-full border-4 border-white shadow-md" />
            </div>
            <div className="p-6 text-center">
              <h3 className="text-xl font-bold text-gray-800 mb-1">Doctor Bahar</h3>
              <span className="inline-block px-3 py-1 text-xs font-semibold bg-pink-100 text-pink-800 rounded-full mb-3">Poetry & Literature</span>
              <div className="flex flex-col space-y-2 text-sm text-gray-600 mb-4">
                <div className="flex items-center justify-center">
                  <FaBookOpen className="text-pink-500 mr-2" />
                  <span>Favorite Book: Divan-e Hafez</span>
                </div>
                <div className="flex items-center justify-center">
                  <FaUtensils className="text-pink-500 mr-2" />
                  <span>Loves: Fesenjan</span>
                </div>
                <div className="flex items-center justify-center">
                  <FaPalette className="text-pink-500 mr-2" />
                  <span>Favorite Color: Crimson</span>
                </div>
              </div>
              <p className="text-gray-700 mb-4">"Poetry is the soul of Persian culture. Let's explore mystical verses while improving your Farsi!"</p>
              <div className="text-xs text-pink-600 bg-pink-50 px-3 py-2 rounded-lg">
                <FaRobot className="mr-1 inline" /> AI Teacher - Always Free
              </div>
            </div>
          </div>
          {/* Teacher 3 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition">
            <div className="h-48 bg-gradient-to-r from-yellow-100 to-orange-100 flex items-center justify-center">
              <img src="https://i.pravatar.cc/150?img=9" alt="Teacher" className="w-32 h-32 rounded-full border-4 border-white shadow-md" />
            </div>
            <div className="p-6 text-center">
              <h3 className="text-xl font-bold text-gray-800 mb-1">Master Darya</h3>
              <span className="inline-block px-3 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full mb-3">Modern Conversation</span>
              <div className="flex flex-col space-y-2 text-sm text-gray-800 mb-4">
                <div className="flex items-center justify-center">
                  <FaBookOpen className="text-gray-800 mr-2" />
                  <span>Favorite Book: My Uncle Napoleon</span>
                </div>
                <div className="flex items-center justify-center">
                  <FaUtensils className="text-gray-800 mr-2" />
                  <span>Loves: Zereshk Polo</span>
                </div>
                <div className="flex items-center justify-center">
                  <FaPalette className="text-gray-800 mr-2" />
                  <span>Favorite Color: Saffron</span>
                </div>
              </div>
              <p className="text-gray-800 mb-4">"Let's chat about daily life in modern Iran while polishing your conversational Farsi skills!"</p>
              <div className="text-xs text-gray-800 bg-gray-100 px-3 py-2 rounded-lg">
                <FaRobot className="mr-1 inline" /> AI Teacher - Always Free
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">How Our AI Teachers Work</h3>
          <p className="text-gray-600 max-w-3xl mx-auto mb-6">Our virtual instructors use advanced AI to provide personalized Farsi lessons anytime, anywhere. They remember your progress, adapt to your learning style, and are always patient!</p>
          <button className="bg-purple-600 text-white px-6 py-3 rounded-full font-medium hover:bg-purple-700 transition">
            Start Learning with a Farsi Teacher Now <FaArrowRight className="ml-2 inline" />
          </button>
        </div>
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Blog Posts */}
          <div className="lg:w-2/3">
            {/* Featured Post */}
            {featured && (
              <div className="bg-white rounded-xl shadow-md overflow-hidden mb-10">
                <div className="md:flex">
                  <div className="md:w-1/2 overflow-hidden">
                    <img src={featured.featured_image} alt={featured.title_en} className="w-full h-full object-cover transition duration-500 blog-image" />
                  </div>
                  <div className="p-8 md:w-1/2">
                    <div className="flex items-center mb-4">
                      <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">{featured.category}</span>
                      <span className="text-gray-500 text-sm">{new Date(featured.published_date).toLocaleDateString()}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{featured.title_en}</h3>
                    <p className="text-gray-600 mb-6">{featured.summary_en}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <img className="w-10 h-10 rounded-full mr-3" src={getAuthorProfilePicture(featured.author_name)} alt="Author" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{featured.author_name}</p>
                          {/* Optionally show author_slug or more info */}
                        </div>
                      </div>
                      <Link to={`/blog/${featured.slug}`} className="text-purple-600 font-medium read-more-btn">Read More <FaArrowRight className="ml-1 inline" /></Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search and Filter */}
            <div className="mb-8 space-y-4">
              {/* Search Input */}
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Category Filter */}
              <div className="flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    className={`px-4 py-2 rounded-full text-sm font-medium category-chip ${activeCategory === cat ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Blog Posts Grid */}
            {currentArticles.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-8">
                {currentArticles.map(article => (
                <Link key={article.id} to={`/blog/${article.slug}`} className="block group">
                  <div className="bg-white rounded-xl shadow-md overflow-hidden blog-card">
                    <div className="h-48 overflow-hidden">
                      <img src={article.featured_image} alt={article.title_en} className="w-full h-full object-cover transition duration-500 blog-image" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center mb-3">
                        <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full mr-2">{article.category}</span>
                        <span className="text-gray-500 text-sm">{new Date(article.published_date).toLocaleDateString()}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-3">{article.title_en}</h3>
                      <p className="text-gray-600 mb-4">{article.summary_en}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <img className="w-8 h-8 rounded-full mr-2" src={getAuthorProfilePicture(article.author_name)} alt="Author" />
                          <p className="text-sm font-medium text-gray-800">{article.author_name}</p>
                        </div>
                        <Link to={`/blog/${article.slug}`} className="text-purple-600 text-sm font-medium read-more-btn">Read More <FaArrowRight className="ml-1 inline" /></Link>
                      </div>
                    </div>
                  </div>
                </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="bg-gray-50 rounded-lg p-8">
                  <FaSearch className="mx-auto text-gray-400 text-xl mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">No articles found</h3>
                  <p className="text-gray-600 mb-4">
                    {search ? `No articles match "${search}"` : `No articles found in category "${activeCategory}"`}
                  </p>
                  <button
                    onClick={() => {
                      setSearch('');
                      setActiveCategory('All');
                    }}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
                  >
                    Clear filters
                  </button>
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-12">
                <nav className="flex items-center space-x-2">
                  {/* Previous button */}
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md border border-gray-300 transition ${
                      currentPage === 1 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <FaChevronLeft />
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                    // Show first page, last page, current page, and pages around current
                    const showPage = page === 1 || 
                                   page === totalPages || 
                                   Math.abs(page - currentPage) <= 1;
                    
                    if (!showPage) {
                      // Show ellipsis for gaps
                      if (page === 2 && currentPage > 4) {
                        return <span key={page} className="px-2 text-gray-500">...</span>;
                      }
                      if (page === totalPages - 1 && currentPage < totalPages - 3) {
                        return <span key={page} className="px-2 text-gray-500">...</span>;
                      }
                      return null;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-md transition ${
                          currentPage === page
                            ? 'bg-purple-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}

                  {/* Next button */}
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md border border-gray-300 transition ${
                      currentPage === totalPages 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <FaChevronRight />
                  </button>
                </nav>
              </div>
            )}

            {/* Show pagination info */}
            <div className="text-center mt-4 text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredArticles.length)} of {filteredArticles.length} articles
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3 space-y-8">
            {/* About Widget */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-800 mb-4">About Pirooz</h3>
              <p className="text-gray-600 mb-4">Welcome to our blog where we explore the rich Persian culture, history, and language. Our mission is to help Farsi learners deepen their understanding of Iran and its traditions. Vajak is your companion on this journey.</p>
              <div className="flex space-x-4">
                <a href="https://facebook.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600"><FaFacebookF /></a>
                <a href="https://twitter.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600"><FaTwitter /></a>
                <a href="https://instagram.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600"><FaInstagram /></a>
                <a href="https://youtube.com/vajakapp" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-600"><FaYoutube /></a>
              </div>
            </div>

            {/* Popular Posts */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Popular Articles</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1601593767171-7375483caca3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Persian calligraphy" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <Link to="/blog" className="text-sm font-medium text-gray-800 hover:text-purple-600">The Art of Persian Calligraphy</Link>
                    <p className="text-xs text-gray-500">April 5, 2023</p>
                  </div>
                </div>
                {/* ... more popular posts ... */}
              </div>
            </div>

            {/* Language Tip */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-6 rounded-xl shadow-md text-white">
              <h3 className="text-xl font-bold mb-4 text-white">Farsi Language Tip</h3>
              <div className="bg-white bg-opacity-20 p-4 rounded-lg mb-4">
                <p className="text-sm mb-2">In Persian, the word for "thank you" is:</p>
                <p className="text-xl font-bold farsi-font mb-2">متشکرم</p>
                <p className="text-sm">pronounced as "motshakkeram"</p>
              </div>
              <p className="text-sm">Try using it today with a Persian speaker!</p>
            </div>

            {/* Newsletter */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Subscribe to Our Newsletter</h3>
              <p className="text-gray-600 mb-4">Get weekly updates on Persian culture, language tips, and learning resources.</p>
              <div className="space-y-3">
                <input
                  type="email"
                  placeholder="Your Email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  disabled={newsletterLoading}
                  required
                />
                <button
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition disabled:opacity-60"
                  onClick={handleNewsletterSubscribe}
                  disabled={newsletterLoading}
                >
                  {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                </button>
                {newsletterStatus && (
                  <p className={`text-sm mt-2 ${newsletterStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{newsletterStatus.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 mt-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FaBookOpen className="text-xl gradient-text" />
                <h3 className="text-2xl font-bold">Vajak</h3>
              </div>
              <p className="text-gray-400">Your gateway to Persian language and culture. Learn Farsi while discovering Iran's rich heritage with Pirooz.</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition">Home</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Blog</Link></li>
                <li><Link to="/pathways" className="text-gray-400 hover:text-white transition">Courses</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-amber-400">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Categories</h4>
              <ul className="space-y-2">
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Language Learning</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Persian History</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Iranian Culture</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Persian Literature</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition">Travel Guides</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2">
                  <FaEnvelope className="text-purple-400" />
                  <a href="mailto:support@pingquack.com" className="text-gray-400 hover:text-white transition">support@pingquack.com</a>
                </li>
                <li className="flex items-center space-x-2">
                  <FaPhoneAlt className="text-purple-400" />
                  <a href="tel:+14245378243" className="text-gray-400 hover:text-white transition">+1 (424) 537-8243</a>
                </li>
                <li className="flex items-center space-x-2">
                  <FaMapMarkerAlt className="text-purple-400" />
                  <span className="text-gray-400">Laguna Hills, CA</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 پیروز Pirooz. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Gradient text style */}
      <style>{`
        .gradient-text {
          background: linear-gradient(90deg, #8B5CF6, #EC4899);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .blog-card:hover .blog-image {
          transform: scale(1.05);
        }
        .farsi-font {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        .read-more-btn {
          transition: all 0.3s ease;
        }
        .read-more-btn:hover {
          transform: translateX(5px);
        }
        .category-chip {
          transition: all 0.2s ease;
        }
        .category-chip:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Blog; 